import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button, Modal, Select } from '@scripsteam/scrips-signal-ds';
import { TagSearchField } from '@/settings/components/tag-search-field';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import {
  useHospitalsSearch,
  useSpecialityCodesSearch,
} from '@/settings/services';
import type { EducationResidencyOrFellowship } from '@/types/settings-api';
import {
  clampEndYearOnStartChange,
  makeEndYearOptions,
  makeStartYearOptions,
} from './year-range';

/**
 * Residency / Fellowship add/edit dialog — PROD-761 commit 12.
 *
 * Single component parameterised on `kind: 'residency' | 'fellowship'`.
 * Flutter ships two near-identical widgets (edit_residency_widget.dart
 * + edit_fellowship_widget.dart) — they differ only in label spelling
 * (ORGANIZATION / Enter organization vs ORGANISATION / Enter organisation)
 * and which array the result attaches to in the parent payload. Same
 * entry type (`EducationResidencyOrFellowship`), same hospital
 * typeahead, same SNOMED speciality typeahead, same year dropdowns.
 *
 * Four mandatory fields:
 *
 *   1. ORGANIZATION / ORGANISATION * — Hospital typeahead (Master/Hospital)
 *   2. SPECIALITY *                  — SNOMED typeahead (Master/PracticeCodeList,
 *                                      same hook used by Professional tab)
 *   3. START YEAR *                  — dropdown, current year ... current-100
 *   4. END YEAR *                    — dropdown, current year ... start year
 *                                      (constrained >= start; clears if start
 *                                      moves above the current end value)
 *
 * Footer: Save / Cancel + two-tap Remove confirmation gate (pattern
 * established on QualificationEditDialog per @samer PR #124 review).
 *
 * Wire-shape preserved verbatim:
 *   - Typo'd `orgnizationId` / `orgnizationName` (NOT `organizationId`).
 *   - `specialityId` is the SNOMED master's `specialitySkillId`.
 *   - `code` is the full StaffSpecialityCode envelope from the SNOMED
 *     master entry (matches the shape on `ProfessionalDetails.speciality`).
 *   - `countryId` / `cityId` stay null — Flutter's edit widgets don't
 *     surface them. Existing values are preserved silently across edit.
 *   - `id: null` on new rows; backend allocates join PK server-side.
 *
 * No soft-delete — same array-replace semantics as Qualifications +
 * Work Experience. Remove drops the row from the local array; the
 * parent's save POST replaces the full `residencies[]` / `fellowships[]`.
 */
export type ResidencyFellowshipKind = 'residency' | 'fellowship';

export interface ResidencyFellowshipEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: ResidencyFellowshipKind;
  initial: EducationResidencyOrFellowship | null;
  onSave: (next: EducationResidencyOrFellowship) => void;
  onRemove?: () => void;
}

function makeEmptyEntry(): EducationResidencyOrFellowship {
  return {
    id: null,
    countryId: null,
    cityId: null,
    orgnizationId: null,
    orgnizationName: '',
    specialityId: null,
    specialityName: '',
    code: {
      code: null,
      displayName: null,
      system: null,
      description: null,
      shortName: null,
    },
    duration: { start: null, end: null },
  };
}

// Flutter label parity (residency=American, fellowship=British). Verbatim
// per the magic-strings rule — these are user-facing strings the backend
// has no opinion on, but the convention is set on the Flutter side and
// we mirror exactly.
const LABELS = {
  residency: {
    orgLabel: 'Organization',
    orgPlaceholder: 'Enter organization',
    addTitle: 'Add Residency',
    editTitle: 'Edit Residency',
  },
  fellowship: {
    orgLabel: 'Organisation',
    orgPlaceholder: 'Enter organisation',
    addTitle: 'Add Fellowship',
    editTitle: 'Edit Fellowship',
  },
} as const;

export function ResidencyFellowshipEditDialog({
  open,
  onOpenChange,
  kind,
  initial,
  onSave,
  onRemove,
}: ResidencyFellowshipEditDialogProps) {
  const isEdit = !!initial && initial.id !== null;
  const labels = LABELS[kind];
  const [draft, setDraft] = useState<EducationResidencyOrFellowship>(
    initial ?? makeEmptyEntry(),
  );
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(initial ?? makeEmptyEntry());
      setConfirmingRemove(false);
    }
  }, [open, initial]);

  // Year bounds computed inline per render (year-drift fix from #124).
  // Shared util `./year-range` after the dedup on #128 review.
  const currentYear = new Date().getFullYear();
  const startYearOptions = makeStartYearOptions(currentYear);
  const endYearOptions = makeEndYearOptions(currentYear, draft.duration.start);

  const handleStartChange = (val: string | null) => {
    const next = val ? Number(val) : null;
    setDraft({
      ...draft,
      duration: {
        start: next,
        end: clampEndYearOnStartChange(next, draft.duration.end),
      },
    });
  };

  const handleEndChange = (val: string | null) => {
    setDraft({
      ...draft,
      duration: { ...draft.duration, end: val ? Number(val) : null },
    });
  };

  const isValid =
    !!draft.orgnizationId &&
    !!draft.specialityId &&
    draft.duration.start !== null &&
    draft.duration.end !== null;

  const handleSave = () => {
    if (!isValid) {
      toast.error(
        `${labels.orgLabel.charAt(0)}${labels.orgLabel.slice(1).toLowerCase()}, speciality, start year, and end year are required.`,
      );
      return;
    }
    onSave(draft);
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={isEdit ? labels.editTitle : labels.addTitle}
      size="lg"
      footer={<><div className="flex items-center gap-2">
            <Button type="button" onClick={handleSave} disabled={!isValid}>
              Save
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
          </div>
          {isEdit && onRemove && (
            <Button
              type="button"
              variant="tertiary"
              onClick={() => {
                if (confirmingRemove) {
                  onRemove();
                  onOpenChange(false);
                } else {
                  setConfirmingRemove(true);
                }
              }}
              className={
                confirmingRemove
                  ? 'bg-status-error text-text-inverse hover:bg-status-error/90'
                  : 'text-status-error hover:text-status-error hover:bg-status-error/10'
              }
            >
              {confirmingRemove ? 'Confirm Remove' : 'Remove'}
            </Button>
          )}</>}
      footerAlign="between"
    >
        

        <div data-slot="settings-dialog-body" className="space-y-4 py-2">
          <HospitalPicker
            label={labels.orgLabel}
            placeholder={labels.orgPlaceholder}
            value={{ id: draft.orgnizationId, name: draft.orgnizationName }}
            onSelect={(picked) =>
              setDraft({
                ...draft,
                orgnizationId: picked.id,
                orgnizationName: picked.name,
              })
            }
          />

          <SpecialityPicker
            value={{ id: draft.specialityId, name: draft.specialityName }}
            onSelect={(picked) =>
              setDraft({
                ...draft,
                specialityId: picked.id,
                specialityName: picked.name,
                code: picked.code,
              })
            }
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel stacked>
                Start year <span className="text-status-error">*</span>
              </FieldLabel>
              <Select
                value={draft.duration.start?.toString() ?? ''}
                onChange={handleStartChange}
                placeholder="Select start year"
                options={startYearOptions.map((y) => ({
                  label: y.toString(),
                  value: y.toString(),
                }))}
              />
            </div>

            <div>
              <FieldLabel stacked>
                End year <span className="text-status-error">*</span>
              </FieldLabel>
              <Select
                value={draft.duration.end?.toString() ?? ''}
                onChange={handleEndChange}
                disabled={draft.duration.start === null}
                placeholder="Select end year"
                options={endYearOptions.map((y) => ({
                  label: y.toString(),
                  value: y.toString(),
                }))}
              />
            </div>
          </div>
        </div>

        
      </Modal>
  );
}

function HospitalPicker({
  label,
  placeholder,
  value,
  onSelect,
}: {
  label: string;
  placeholder: string;
  value: { id: string | null; name: string };
  onSelect: (picked: { id: string; name: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value.name);
  const debouncedSearch = useDebounce(search, 250);
  const { data: results = [], isLoading } = useHospitalsSearch(debouncedSearch);

  const options = useMemo(
    () => results.map((r) => ({ id: r.Id, label: r.HospitalName })),
    [results],
  );

  const handleSelect = (id: string) => {
    const found = results.find((r) => r.Id === id);
    if (!found) return;
    setSearch(found.HospitalName);
    onSelect({ id: found.Id, name: found.HospitalName });
    setOpen(false);
  };

  return (
    <div>
      <FieldLabel stacked>
        {label} <span className="text-status-error">*</span>
      </FieldLabel>
      <TagSearchField
        value={search}
        placeholder={placeholder}
        open={open}
        onOpenChange={setOpen}
        onSearchChange={setSearch}
        loading={isLoading}
        options={options}
        onSelect={handleSelect}
      />
    </div>
  );
}

function SpecialityPicker({
  value,
  onSelect,
}: {
  value: { id: string | null; name: string };
  onSelect: (picked: {
    id: string;
    name: string;
    code: EducationResidencyOrFellowship['code'];
  }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value.name);
  const debouncedSearch = useDebounce(search, 250);
  const { data: results = [], isLoading } = useSpecialityCodesSearch(debouncedSearch);

  const options = useMemo(
    () =>
      results.map((r) => ({
        id: r.specialitySkillId,
        label: r.code.displayName ?? r.code.code ?? r.specialitySkillId,
        secondary: r.code.code ?? undefined,
      })),
    [results],
  );

  const handleSelect = (id: string) => {
    const found = results.find((r) => r.specialitySkillId === id);
    if (!found) return;
    const name = found.code.displayName ?? found.code.code ?? '';
    setSearch(name);
    // Wire-shape null-fill on `code.description` + `code.shortName`.
    //
    // The Professional tab's `useSpecialityCodesSearch` returns
    // `StaffSpeciality` whose `code` is the 3-field shape
    // `{code, displayName, system}` — that's the wire shape the
    // SNOMED master ships back.
    //
    // `EducationResidencyOrFellowship.code` is the **wider** 5-field
    // `StaffSpecialityCode` (`+ description + shortName`). The master
    // never sources values for those two extras — so we fill them
    // with `null` on master select. This matches the shape of
    // existing residency records on the wire (verified against the
    // PROD-761 commit 4 GET response captured 2026-05-13) and
    // matches the deviation @samer signed off on at PR #124 review
    // ("flag any wire shape deviations in those PRs rather than
    // silently adapting" — flagged on this PR's description).
    //
    // If a future SNOMED master ever DOES ship description / shortName,
    // remove the explicit null fill and spread the whole `found.code`.
    // Until then, the explicit null is load-bearing — it ensures
    // JSON.stringify emits the keys (only `undefined` would be stripped),
    // which the .NET model binder relies on to populate the join row.
    onSelect({
      id: found.specialitySkillId,
      name,
      code: {
        ...found.code,
        description: null,
        shortName: null,
      },
    });
    setOpen(false);
  };

  return (
    <div>
      <FieldLabel stacked>
        Speciality <span className="text-status-error">*</span>
      </FieldLabel>
      <TagSearchField
        value={search}
        placeholder="Enter speciality"
        open={open}
        onOpenChange={setOpen}
        onSearchChange={setSearch}
        loading={isLoading}
        options={options}
        onSelect={handleSelect}
      />
    </div>
  );
}
